// 🔒 LOCKED: Extras & Credits system
// Do not modify structure, UI, or logic without review

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import { supabase } from '../../../lib/supabase';
import SubcontractCombobox from '../../../components/ui/SubcontractCombobox';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import { getCurrentOrgName } from '../../../lib/getCurrentOrgId';
import { formatCurrency as fmtCAD } from '../../../utils/formatCurrency';
import { DIVISION_OPTIONS } from '../../../constants/divisions';
import { coFieldsFor, isCoRequired } from '../../../services/projectExtrasService';
import useEscapeKey from '../../../hooks/useEscapeKey';
import EntityDocumentsPanel from './EntityDocumentsPanel';

// Partie haute (lot X2) : meme style de champ que DirectiveModal, a la hauteur
// exacte du selecteur de division (SearchableSelect « md » : 34 px).
const TOP_FIELD = 'w-full h-[34px] px-3 py-2 text-xs leading-4 border rounded-lg outline-none transition-colors placeholder:text-gray-400 placeholder:font-normal';
const TOP_LABEL = 'block text-[11px] font-medium text-[#FF6B35] mb-1';
const DESCRIPTION_MIN_H = 76;

// Zone de texte qui grandit avec son contenu : plus de barre de defilement,
// tout le texte reste visible. A brancher en ref (rappelee a chaque rendu).
const autoGrow = (el, minHeight = 0) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.max(minHeight, el.scrollHeight + 2)}px`;
};

// Generate a lightweight unique ID for groupId (UI-only, never persisted)
const newGroupId = () => `g_${Date.now()}_${Math.random()?.toString(36)?.slice(2, 7)}`;

// subcontractName reste null tant qu'aucun sous-traitant n'est choisi : c'est
// une VALEUR stockee, pas un libelle. L'invite de choix vit dans le combobox.
const EMPTY_LINE = () => ({ groupId: newGroupId(), subcontractId: null, subcontractName: null, description: '', amount: 0 });

// t() vient du hook : il ne peut pas etre appele au niveau module. On passe le
// traducteur en parametre et on appelle depuis le composant.
const getStatusOptions = (t) => [
  { value: 'draft', label: t('status.draft') },
  { value: 'submitted', label: t('status.submitted') },
  { value: 'to_resubmit', label: t('status.to_resubmit') },
  { value: 'approved', label: t('status.approved') },
  { value: 'rejected', label: t('status.rejected') }
];

const CATEGORY_OPTIONS = [
  { value: 'contractual', key: 'contractual' },
  { value: 'backcharge', key: 'backcharge' },
  { value: 'internal_error', key: 'internal_error' },
  { value: 'absorbed_tm', key: 'absorbed_tm' },
  { value: 'dispute', key: 'dispute' },
];

// Montant d'une ligne, affiche a la quebecoise : « 9 158,88 $ » (symbole a
// droite, espace pour les milliers, virgule decimale). En saisie, on accepte la
// virgule comme le point ; la valeur transmise reste un nombre.
const fmtLineAmount = (value) => {
  const n = Number(value) || 0;
  if (n === 0) return '';
  return new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })?.format(n);
};
const parseLineAmount = (raw) => {
  const cleaned = String(raw || '')?.replace(/[\s\u00A0\u202F$]/g, '')?.replace(',', '.');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return 0;
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? 0 : n;
};
// Signe par defaut = celui du type : en Credit, un nombre tape sans signe est un
// credit (negatif). « + » ou « − » tapes devant forcent le signe.
const parseSignedLineAmount = (raw, defaultNegative) => {
  const text = String(raw || '')?.trim();
  const n = Math.abs(parseLineAmount(text));
  if (text?.startsWith('-') || text?.startsWith('−')) return -n;
  if (text?.startsWith('+')) return n;
  return defaultNegative ? -n : n;
};
const LineAmountInput = ({ value, onChange, defaultNegative = false }) => {
  const [draft, setDraft] = useState(null); // texte en cours de saisie (null = affichage)
  return (
    <div className="relative w-28 flex-shrink-0">
      <input
        type="text"
        inputMode="decimal"
        value={draft ?? fmtLineAmount(value)}
        onFocus={(e) => {
          const n = Number(value) || 0;
          // Signe explicite en saisie : rien ne change si on ressort sans taper.
          const plus = n > 0 && defaultNegative ? '+' : '';
          setDraft(n === 0 ? '' : `${plus}${String(n)?.replace('.', ',')}`);
          const el = e?.target;
          setTimeout(() => el?.select(), 0);
        }}
        onChange={(e) => {
          const raw = e?.target?.value?.replace(/[^0-9,.\-−+\s]/g, '');
          setDraft(raw);
          onChange?.(parseSignedLineAmount(raw, defaultNegative));
        }}
        onBlur={() => setDraft(null)}
        placeholder="0,00"
        className="w-full pl-2 pr-5 py-1.5 text-xs text-right tabular-nums border border-gray-200 rounded-md outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 placeholder:text-gray-400 placeholder:text-xs placeholder:font-normal"
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none">$</span>
    </div>
  );
};

const AddExtraModal = ({ projectId, subcontracts = [], onClose, onSave, isEditMode = false, extra = null, startInView = false }) => {
  const { t } = useTranslation();
  const categoryRef = useRef(null);
  // Consultation (clic sur une ligne du tableau) : la meme modale, champs
  // verrouilles, « Modifier » en bas — comme la fiche de directive.
  const [isViewing, setIsViewing] = useState(!!(startInView && isEditMode && extra));
  // Un extra approuve ne se modifie pas (meme regle que le menu ⋮).
  const editLocked = extra?.status === 'approved';
  const [form, setForm] = useState({
    type: extra?.type || 'extra',
    title: extra?.title || '',
    description: extra?.description || '',
    status: extra?.status || 'draft',
    division: extra?.division || '',
    coNumber: extra?.coNumber || extra?.co_number || '',
    receivedDate: extra?.receivedDate || extra?.received_date || '',
    submittedDate: extra?.submittedDate || extra?.submitted_date || '',
  });
  const [category, setCategory] = useState(extra?.category || null);
  const [lines, setLines] = useState([EMPTY_LINE()]);
  const [costAccepted, setCostAccepted] = useState(
    extra?.cost_accepted != null ? Number(extra?.cost_accepted) : 0
  );
  // profitInput stores the dollar amount (persisted to DB as-is)
  const [profitInput, setProfitInput] = useState(
    extra?.profit != null ? Number(extra?.profit) : 0
  );
  // profitPercent is UI-only; initialized after lines load in edit mode
  const [profitPercent, setProfitPercent] = useState(0);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingLines, setLoadingLines] = useState(isEditMode);
  const [orgName, setOrgName] = useState('');

  // Document upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const storagePath = extra?.id ? `extra/${extra?.id}` : null;

  // Echap ferme la modale (sauf pendant l'enregistrement).
  useEscapeKey(onClose, !saving);

  // Extra issu d'une directive : son numero est celui de la directive. Pour une
  // categorie contractuelle, il s'affiche en lecture seule.
  const directiveId = extra?.directiveId || extra?.directive_id || null;
  const [directiveNomenclature, setDirectiveNomenclature] = useState(null);
  useEffect(() => {
    if (!directiveId) return undefined;
    let alive = true;
    supabase?.from('project_directives')?.select('nomenclature')?.eq('id', directiveId)?.maybeSingle()
      ?.then(({ data }) => { if (alive) setDirectiveNomenclature(data?.nomenclature || null); });
    return () => { alive = false; };
  }, [directiveId]);


  useEffect(() => {
    getCurrentOrgName()?.then(name => {
      if (name) setOrgName(name);
    });
  }, []);

  // Load existing lines when in edit mode
  useEffect(() => {
    if (isEditMode && extra?.id) {
      setLoadingLines(true);
      supabase?.from('project_extra_lines')?.select('*')?.eq('extra_id', extra?.id)?.then(({ data, error }) => {
          if (!error && data && data?.length > 0) {
            // Group DB lines by subcontract_id so each subcontractor gets one groupId
            // This preserves the visual grouping on re-open
            const groupMap = {};
            const groupOrder = [];
            data?.forEach(l => {
              const isInternal = l?.is_internal === true;
              const subId = isInternal ? '__internal__' : (l?.subcontract_id || null);
              const mapKey = subId ?? '__null__';
              if (!groupMap?.[mapKey]) {
                groupMap[mapKey] = newGroupId();
                groupOrder?.push(mapKey);
              }
            });

            const loadedLines = data?.map(l => {
              const isInternal = l?.is_internal === true;
              const subId = isInternal ? '__internal__' : (l?.subcontract_id || null);
              const mapKey = subId ?? '__null__';
              let subName = null;
              if (subId === '__internal__') {
                const internalLabel = orgName ? `${orgName} ${t('internal.suffix')}` : '';
                subName = internalLabel;
              } else if (subId) {
                const found = subcontracts?.find(s => s?.id === subId);
                subName = found?.subcontractor?.companyName || found?.subcontractors?.company_name || found?.name || subId;
              }
              return {
                id: l?.id,
                groupId: groupMap?.[mapKey],
                subcontractId: subId,
                subcontractName: subName,
                description: l?.description || '',
                // Stockee relative au type (positif = dans le sens du type) ;
                // affichee avec son vrai signe : un credit s'affiche negatif.
                amount: (Number(l?.amount) || 0) * (extra?.type === 'credit' ? -1 : 1)
              };
            });
            setLines(loadedLines);

            // Reverse-calculate profit % from stored $ and |total|
            const loadedTotal = Math.abs(loadedLines?.reduce((sum, l) => sum + Number(l?.amount || 0), 0));
            const storedProfit = extra?.profit != null ? Number(extra?.profit) : 0;
            if (loadedTotal > 0) {
              setProfitPercent(parseFloat(((storedProfit / loadedTotal) * 100)?.toFixed(4)));
            } else {
              setProfitPercent(0);
            }
          } else {
            setLines([EMPTY_LINE()]);
            setProfitPercent(0);
          }
          setLoadingLines(false);
        })?.catch(() => {
          setLines([EMPTY_LINE()]);
          setProfitPercent(0);
          setLoadingLines(false);
        });
    }
  }, [isEditMode, extra?.id]);

  // Mode modification / consultation : les pieces passent par
  // EntityDocumentsPanel (table extra_documents). Ici ne reste que la creation :
  // les fichiers attendent l'enregistrement de l'extra (il faut son id).
  const handleFileInputChange = (e) => {
    // In create mode, queue files to upload after save — store locally
    setPendingFiles(prev => [...prev, ...Array.from(e?.target?.files)]);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  // Pending files for create mode (uploaded after entity is created)
  const [pendingFiles, setPendingFiles] = useState([]);

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    if (isViewing) return;
    setPendingFiles(prev => [...prev, ...Array.from(e?.dataTransfer?.files)]);
  };

  const handleDragOver = (e) => { e?.preventDefault(); setIsDragOver(true); };
  const handleDragEnter = (e) => { e?.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);

  const totalExtra = lines?.reduce((sum, l) => sum + Number(l?.amount || 0), 0);
  const totalSubmitted = lines?.reduce((sum, line) => sum + (Number(line?.amount) || 0), 0);
  const profit = costAccepted - totalSubmitted;

  // Lignes signees (vrai signe) : le total peut etre negatif = un credit.
  // Profit sur la valeur absolue, du signe du total ; stocke positif.
  const totalSign = totalExtra < 0 ? -1 : 1;
  const totalAbs = Math.abs(totalExtra);
  const profitDollar = totalAbs > 0 ? totalAbs * (Number(profitPercent || 0) / 100) : 0;

  // Keep profitInput in sync with profitDollar for persistence
  useEffect(() => {
    setProfitInput(profitDollar);
  }, [profitDollar]);

  const grandTotal = totalSubmitted + totalSign * profitDollar;

  // Bascule EN DIRECT : le signe du total des lignes decide du type (Extra si
  // positif, Credit si negatif). A 0, le type courant reste.
  useEffect(() => {
    if (totalExtra < 0 && form?.type !== 'credit') setForm(prev => ({ ...prev, type: 'credit' }));
    else if (totalExtra > 0 && form?.type !== 'extra') setForm(prev => ({ ...prev, type: 'extra' }));
  }, [totalExtra, form?.type]);

  // Clic sur le segment : on retourne le signe de toutes les lignes, pour que
  // le type choisi et le total restent d'accord (la bascule auto suit).
  const handleTypeClick = (typeVal) => {
    if (typeVal === form?.type) return;
    const hasAmounts = lines?.some(l => Number(l?.amount) !== 0);
    if (hasAmounts) setLines(prev => prev?.map(l => ({ ...l, amount: -(Number(l?.amount) || 0) })));
    handleChange('type', typeVal);
  };

  // Enregistrement : retour au stockage relatif au type.
  const storedLineAmount = (amount, type) => (Number(amount) || 0) * (type === 'credit' ? -1 : 1);

  const isCredit = form?.type === 'credit';
  const modalTitle = isViewing
    ? (isCredit ? t('extras.viewCredit') : t('extras.viewExtra'))
    : isEditMode
      ? (isCredit ? t('extras.editCredit') : t('extras.editExtra'))
      : (isCredit ? t('extras.addCredit') : t('extras.addExtra'));

  const validate = () => {
    const newErrors = {};
    if (!form?.title?.trim()) newErrors.title = t('extras.addModal.titleRequired');
    if (!lines || lines?.length === 0) newErrors.lines = t('extras.addModal.linesRequired');
    if (!category) newErrors.category = t('extras.category.required');

    // Chaque groupe doit porter un sous-traitant reel ou le marqueur interne.
    // Sans ca, la ligne part avec subcontract_id NULL et is_internal false : ni
    // facturable a un sous-traitant, ni approuvable sans avenant.
    if (lines && lines?.length > 0) {
      const groupes = getGroups();
      const sansSousTraitant = groupes
        ?.map((g, i) => ({ ...g, rang: i + 1 }))
        ?.filter(g => !g?.subcontractId);
      if (sansSousTraitant?.length > 0) {
        const rangs = sansSousTraitant?.map(g => `#${g?.rang}`)?.join(', ');
        newErrors.lines = `${t('extra.errorSubcontractorRequired')} (${rangs})`;
      }
    }
    return newErrors;
  };

  // Categorie interne, a la creation : n° de directive « Interne » par defaut
  // s'il est vide (toujours modifiable). Retour a contractuel : on retire ce
  // defaut s'il n'a pas ete touche.
  const internalCoDefault = t('extras.co.internalDefault');
  const handleCategorySelect = (val) => {
    setCategory(val);
    if (errors?.category) setErrors(prev => ({ ...prev, category: '' }));
    if (!isEditMode) {
      const current = form?.coNumber?.trim();
      if (!isCoRequired(val) && !current) handleChange('coNumber', internalCoDefault);
      else if (isCoRequired(val) && current === internalCoDefault) handleChange('coNumber', '');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      if (validationErrors?.category && categoryRef?.current) {
        categoryRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setSaving(true);
    try {
      if (isEditMode && extra?.id) {
        const payload = {
          type: form?.type,
          title: form?.title?.trim(),
          description: form?.description?.trim() || null,
          status: form?.status,
          amount: Math.abs(totalSubmitted),
          division: form?.division || null,
          co_number: form?.coNumber?.trim() || null,
          received_date: form?.receivedDate || null,
          submitted_date: form?.submittedDate || null,
          cost_submitted: Math.abs(totalSubmitted),
          cost_accepted: Number(costAccepted) || 0,
          profit: Number(profitInput) || 0,  // persists the calculated $ amount
          category: category || null,
        };
        // ODC : meme regle que le service (statut + categorie), dans le meme UPDATE.
        Object.assign(payload, coFieldsFor({
          status: payload?.status,
          category: payload?.category,
          coStatus: extra?.coStatus ?? extra?.co_status ?? null,
        }));
        const { error: updateError } = await supabase?.from('project_extras')?.update(payload)?.eq('id', extra?.id);
        if (updateError) throw updateError;

        const { error: deleteError } = await supabase?.from('project_extra_lines')?.delete()?.eq('extra_id', extra?.id);
        if (deleteError) throw deleteError;

        if (lines && lines?.length > 0) {
          const lineRows = lines?.map(l => {
            const isInternal = l?.subcontractId === '__internal__';
            const subcontractUUID = (!isInternal && l?.subcontractId) ? l?.subcontractId : null;
            return {
              extra_id: extra?.id,
              subcontract_id: subcontractUUID,
              is_internal: isInternal,
              description: l?.description || null,
              amount: storedLineAmount(l?.amount, form?.type)
              // groupId intentionally NOT persisted — UI only
            };
          });
          const { error: insertError } = await supabase?.from('project_extra_lines')?.insert(lineRows);
          if (insertError) throw insertError;
        }

        const { data: updatedExtra } = await supabase?.from('project_extras')?.select('*')?.eq('id', extra?.id)?.single();
        await onSave(updatedExtra || { ...extra, amount: Math.abs(totalSubmitted) });
      } else {
        const processedLines = lines?.map(l => ({
          subcontractId: l?.subcontractId,
          subcontractName: l?.subcontractName,
          description: l?.description,
          amount: storedLineAmount(l?.amount, form?.type)
          // groupId intentionally NOT included — UI only
        }));

        const payload = {
          projectId,
          type: form?.type,
          title: form?.title?.trim(),
          description: form?.description?.trim() || null,
          amount: Math.abs(totalSubmitted),
          status: form?.status,
          division: form?.division || null,
          coNumber: form?.coNumber?.trim() || (category && !isCoRequired(category) ? internalCoDefault : null),
          receivedDate: form?.receivedDate || null,
          submittedDate: form?.submittedDate || null,
          costSubmitted: Math.abs(totalSubmitted),
          costAccepted: Number(costAccepted) || 0,
          profit: Number(profitInput) || 0,  // persists the calculated $ amount
          category: category || null,
          lines: processedLines,
          pendingFiles: pendingFiles
        };
        await onSave(payload);
      }
      onClose();
    } catch (err) {
      console.error('Extra save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleLineChange = (index, field, value) => {
    setLines(prev => prev?.map((line, i) => i === index ? { ...line, [field]: value } : line));
  };

  const addLine = () => setLines(prev => [...prev, EMPTY_LINE()]);

  const removeLine = (index) => {
    if (lines?.length === 1) return;
    setLines(prev => prev?.filter((_, i) => i !== index));
  };

  // --- Grouped handlers (UI only — flat `lines` array unchanged) ---

  // Add a sub-line to an existing group identified by its STABLE groupId
  const addLineToGroup = (groupId, groupSubcontractId, groupSubcontractName) => {
    setLines(prev => [
      ...prev,
      {
        groupId,  // same groupId → same block
        subcontractId: groupSubcontractId,
        subcontractName: groupSubcontractName || null,
        description: '',
        amount: 0
      }
    ]);
  };

  // Add a brand-new subcontractor block — always gets a FRESH groupId
  // so it never merges with an existing null-subcontractor block
  const addSubcontractor = () => {
    setLines(prev => [...prev, EMPTY_LINE()]);  // EMPTY_LINE() calls newGroupId() each time
  };

  // Remove a single sub-line by its index in the flat array
  const removeLineByIndex = (index) => {
    if (lines?.length === 1) return;
    setLines(prev => prev?.filter((_, i) => i !== index));
  };

  // Change the subcontractor for ALL lines in a group (identified by groupId)
  const updateGroupSubcontract = (groupId, newVal) => {
    let newName = null;
    if (newVal && newVal !== '__internal__') {
      const found = subcontracts?.find(s => s?.id === newVal);
      newName = found?.subcontractor?.companyName || found?.subcontractors?.company_name || found?.name || newVal;
    } else if (newVal === '__internal__') {
      newName = orgName ? `${orgName} ${t('internal.suffix')}` : '';
    }
    setLines(prev => prev?.map(line =>
      line?.groupId === groupId
        ? { ...line, subcontractId: newVal || null, subcontractName: newName }
        : line
    ));
  };

  // Derive ordered groups from flat lines — grouped by STABLE groupId (not subcontractId)
  const getGroups = () => {
    const seen = [];
    const map = {};
    lines?.forEach((line, idx) => {
      const key = line?.groupId;
      if (!map?.[key]) {
        map[key] = [];
        seen?.push(key);
      }
      map?.[key]?.push({ line, idx });
    });
    return seen?.map(key => ({
      groupId: key,
      subcontractId: map?.[key]?.[0]?.line?.subcontractId,
      subcontractName: map?.[key]?.[0]?.line?.subcontractName || null,
      entries: map?.[key]
    }));
  };

  const formatCurrency = (val) => fmtCAD(val);

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const getFileTypeIcon = (name) => {
    const ext = name?.split('.')?.pop()?.toLowerCase();
    if (ext === 'pdf') return <Icon name="FileText" size={14} className="text-red-400 flex-shrink-0" />;
    if (['xls', 'xlsx']?.includes(ext)) return <Icon name="FileSpreadsheet" size={14} className="text-green-500 flex-shrink-0" />;
    if (['doc', 'docx']?.includes(ext)) return <Icon name="FileText" size={14} className="text-blue-500 flex-shrink-0" />;
    return <Icon name="File" size={14} className="text-gray-400 flex-shrink-0" />;
  };

  // Creation : retirer un fichier en attente (rien n'est encore envoye).
  const deleteDocFile = (fileName) => {
    setPendingFiles(prev => prev?.filter(f => f?.name !== fileName));
  };

  // Liste affichee en creation : les fichiers en attente.
  const displayFiles = pendingFiles?.map(f => ({ name: f?.name, metadata: { size: f?.size } }));

  // Status pill color helper
  const getStatusPillClass = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700 border border-green-200';
      case 'submitted': return 'bg-blue-100 text-blue-700 border border-blue-200';
      case 'to_resubmit': return 'bg-amber-100 text-amber-700 border border-amber-200';
      case 'rejected': return 'bg-red-100 text-red-700 border border-red-200';
      default: return 'bg-gray-100 text-gray-600 border border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[1040px] flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <Icon name={isViewing ? 'FileText' : isEditMode ? 'Pencil' : 'Plus'} size={16} className="text-primary" />
              <h2 className="text-sm font-semibold text-gray-900">{modalTitle}</h2>
            </div>
            <p className="text-xs text-gray-400 mt-0.5 ml-6 truncate max-w-[640px]">
              {isViewing
                ? (extra?.title || '—')
                : isCredit ? t('extras.addModal.subtitleCredit') : t('extras.addModal.subtitle')}
            </p>
          </div>
          {/* Status dropdown — top-right of header, always visible */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-500 whitespace-nowrap">{t('extras.editModal.statusLabel')} :</span>
              {isViewing ? (
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${getStatusPillClass(form?.status)}`}>
                  {getStatusOptions(t)?.find(opt => opt?.value === form?.status)?.label || form?.status}
                </span>
              ) : (
                <select
                  value={form?.status}
                  onChange={e => handleChange('status', e?.target?.value)}
                  className={`text-xs font-medium px-2 py-1 rounded-full border outline-none cursor-pointer transition-colors ${getStatusPillClass(form?.status)}`}
                >
                  {getStatusOptions(t)?.map(opt => (
                    <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                  ))}
                </select>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
          {/* En consultation, tout le contenu (sauf les documents) est verrouille
              d'un bloc ; les champs gardent leur apparence normale. */}
          <fieldset
            disabled={isViewing}
            className="min-w-0 space-y-4 [&_:disabled]:cursor-default [&_input:disabled]:opacity-100 [&_input:disabled]:bg-white [&_input:disabled]:text-gray-900 [&_textarea:disabled]:bg-white [&_textarea:disabled]:text-gray-900"
          >

          {/* ── Partie haute (lot X2) ───────────────────────────────────────── */}
          <div className="space-y-5 pb-1">
            {/* 1. Type : segment + Extra / − Credit, seul sur sa ligne */}
            <div className="inline-flex w-full max-w-[520px] p-1 bg-gray-100 rounded-lg" role="radiogroup" aria-label={t('extras.addModal.typeLabel')}>
              {['extra', 'credit']?.map(typeVal => {
                const active = form?.type === typeVal;
                return (
                  <button
                    key={typeVal}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => handleTypeClick(typeVal)}
                    className={`flex-1 h-[30px] rounded-md text-xs font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      active
                        ? typeVal === 'extra'
                          ? 'bg-green-600 text-white shadow-sm'
                          : 'bg-red-600 text-white shadow-sm'
                        : typeVal === 'credit'
                          ? 'text-red-600 hover:bg-white/60'
                          : 'text-green-700 hover:bg-white/60'
                    }`}
                  >
                    {typeVal === 'extra' ? t('extras.addModal.typeExtra') : t('extras.addModal.credit')}
                  </button>
                );
              })}
            </div>

            {/* 2. Categorie : pastilles sur toute la largeur */}
            <div ref={categoryRef}>
              <label className={TOP_LABEL}>
                {t('extras.category.label')} <span className="text-red-500">*</span>
              </label>
              <div className={`flex flex-wrap gap-2 ${errors?.category ? 'p-2 rounded-lg border border-red-300 bg-red-50' : ''}`}>
                {CATEGORY_OPTIONS?.map(opt => (
                  <button
                    key={opt?.value}
                    type="button"
                    onClick={() => handleCategorySelect(opt?.value)}
                    className={`h-8 px-3.5 rounded-full text-xs font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      category === opt?.value
                        ? 'bg-primary border-primary text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t(`extras.category.${opt?.key}`)}
                  </button>
                ))}
              </div>
              {errors?.category && (
                <p className="mt-1 text-xs text-red-500">{errors?.category}</p>
              )}
            </div>

            {/* 3. Division · Numero de directive. (Pas de date de soumission :
                ce champ n'est pas affiche dans la modale aujourd'hui.) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={TOP_LABEL}>{t('extras.addModal.divisionLabel')}</label>
                <SearchableSelect
                  options={DIVISION_OPTIONS?.map(opt => ({ value: opt?.value, label: t(`extras.divisions.${opt?.key}`) }))}
                  value={form?.division || null}
                  onChange={val => handleChange('division', val || '')}
                  placeholder={t('extras.addModal.divisionPlaceholder')}
                  allowNone={true}
                  size="md"
                />
              </div>
              <div>
                <label className={TOP_LABEL}>{t('extras.addModal.coNumberLabel')}</label>
                {directiveNomenclature && isCoRequired(category) ? (
                  <div
                    className="h-[34px] flex items-center gap-1.5 px-3 text-xs rounded-lg border border-gray-100 bg-gray-50 text-gray-700 min-w-0"
                    title={t('extras.addModal.coNumberFromDirective')}
                  >
                    <Icon name="Lock" size={11} className="text-gray-400 flex-shrink-0" />
                    <span className="truncate">{directiveNomenclature}</span>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={form?.coNumber}
                    onChange={e => handleChange('coNumber', e?.target?.value)}
                    placeholder={t('extras.addModal.coNumberPlaceholder')}
                    className={`${TOP_FIELD} border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20`}
                  />
                )}
              </div>
            </div>

            {/* 4. Titre */}
            <div>
              <label className={TOP_LABEL}>
                {t('extras.addModal.titleLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form?.title}
                onChange={e => handleChange('title', e?.target?.value)}
                placeholder={t('extras.addModal.titlePlaceholder')}
                className={`${TOP_FIELD} ${
                  errors?.title ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20'
                }`}
              />
              {errors?.title && <p className="mt-1 text-xs text-red-500">{errors?.title}</p>}
            </div>

            {/* 5. Description des travaux : compacte, grandit avec le texte */}
            <div>
              <label className={TOP_LABEL}>
                {t('extras.addModal.workDescriptionLabel')} <span className="text-gray-400 font-normal">{t('extras.addModal.optional')}</span>
              </label>
              <textarea
                ref={(el) => autoGrow(el, DESCRIPTION_MIN_H)}
                value={form?.description}
                onChange={e => handleChange('description', e?.target?.value)}
                placeholder={t('extras.addModal.descriptionPlaceholder')}
                className="w-full px-3 py-2.5 text-xs !leading-relaxed border border-gray-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-none overflow-hidden placeholder:text-gray-400 placeholder:font-normal"
                style={{ minHeight: `${DESCRIPTION_MIN_H}px` }}
              />
            </div>
          </div>

          {/* 6. Lignes (sous-traitants) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-[#FF6B35]">{isCredit ? t('extras.addModal.creditLines') : t('extras.addModal.extraLines')}</label>
            </div>

            {loadingLines ? (
              <div className="flex items-center justify-center py-6">
                <Icon name="Loader2" size={20} className="animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {getGroups()?.map((group) => {
                    return (
                      <div key={group?.groupId} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Group header — SubcontractCombobox */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                          <Icon name="Building2" size={13} className="text-gray-400 flex-shrink-0" />
                          <div className="flex-1">
                            <SubcontractCombobox
                              subcontracts={subcontracts}
                              value={group?.subcontractId === '__internal__' ? '__internal__' : (group?.subcontractId || null)}
                              onChange={val => updateGroupSubcontract(group?.groupId, val || null)}
                              size="sm"
                              includeInternal={true}
                              allowAll={false}
                              placeholder={t('extra.selectSubcontractorRequired')}
                            />
                          </div>
                        </div>

                        {/* Sub-lines */}
                        <div className="divide-y divide-gray-100">
                          {group?.entries?.map(({ line, idx }) => (
                            <div key={idx} className="flex items-start gap-2 px-3 py-2">
                              {/* Description — large, left */}
                              <textarea
                                ref={(el) => autoGrow(el)}
                                value={line?.description}
                                onChange={e => handleLineChange(idx, 'description', e?.target?.value)}
                                placeholder={t('extras.addModal.descriptionLabel')}
                                rows={2}
                                className="flex-1 text-xs border border-gray-200 rounded-md outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 text-gray-600 px-2.5 py-1.5 resize-none overflow-hidden placeholder:text-gray-400 placeholder:text-xs placeholder:font-normal"
                              />
                              {/* Amount — right, same height */}
                              <LineAmountInput
                                value={line?.amount}
                                defaultNegative={isCredit}
                                onChange={(amount) => handleLineChange(idx, 'amount', amount)}
                              />
                              {/* Delete sub-line */}
                              <button
                                type="button"
                                onClick={() => removeLineByIndex(idx)}
                                disabled={lines?.length === 1}
                                className="p-1 mt-1 rounded text-gray-300 hover:text-red-400 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                              >
                                <Icon name="Trash2" size={13} />
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Add sub-line inside this block */}
                        <div className="px-3 py-2 bg-gray-50 border-t border-gray-100">
                          <button
                            type="button"
                            onClick={() => addLineToGroup(group?.groupId, group?.subcontractId, group?.subcontractName)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-dark transition-colors"
                          >
                            <Icon name="Plus" size={11} />
                            {t('extras.addModal.addLine')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add subcontractor block */}
                <div className="flex items-center justify-between mt-3">
                  <button
                    type="button"
                    onClick={addSubcontractor}
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 border border-dashed border-gray-300 hover:border-gray-400 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Icon name="Plus" size={12} />
                    Ajouter un sous-traitant
                  </button>
                </div>
                {errors?.lines && <p className="mt-1.5 text-xs text-red-500">{errors?.lines}</p>}

                {/* Lines pre-tax total */}
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                  <span className="text-xs font-medium text-gray-500">{t('extras.addModal.linesTotalLabel')}</span>
                  <span className="text-xs font-semibold text-gray-700">{formatCurrency(totalSubmitted)}</span>
                </div>
              </>
            )}
          </div>

          {/* Profit & Total — compact right-aligned summary block */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-end">
              <div className="w-72">
                {/* Row 1 — Total Extra */}
                <div className="flex items-center justify-between py-1 gap-4">
                  <span className="text-xs font-medium text-[#FF6B35] whitespace-nowrap">{isCredit ? 'Total Credit:' : 'Total Extra:'}</span>
                  <span className="text-xs font-semibold text-primary">{formatCurrency(totalExtra)}</span>
                </div>
                {/* Row 2 — Profit */}
                <div className="flex items-center justify-between py-1 gap-4">
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-xs font-medium text-[#FF6B35] whitespace-nowrap">Profit:</span>
                    {/* % input — compact, no leading zeros */}
                    <div className="relative w-16 flex-shrink-0">
                      <input
                        type="number"
                        value={profitPercent === 0 ? '' : profitPercent}
                        onChange={(e) => {
                          const raw = e?.target?.value;
                          if (raw === '' || raw === '-') {
                            setProfitPercent(0);
                            return;
                          }
                          const parsed = parseFloat(raw);
                          setProfitPercent(isNaN(parsed) ? 0 : parsed);
                        }}
                        onFocus={e => e?.target?.select()}
                        placeholder="0"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        className="w-full pl-2 pr-5 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors placeholder:text-gray-400 placeholder:text-xs placeholder:font-normal"
                      />
                      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none">%</span>
                    </div>
                  </div>
                  {/* Read-only $ amount — right-aligned, same column as other amounts */}
                  <span className="text-xs font-semibold text-gray-700">{formatCurrency(totalSign * profitDollar)}</span>
                </div>
                {/* Row 3 — Grand Total */}
                <div className="flex items-center justify-between py-1 border-t border-gray-100 mt-1 pt-2 gap-4">
                  <span className="text-xs font-medium text-[#FF6B35] whitespace-nowrap">Total:</span>
                  <span className={`text-xs font-semibold ${grandTotal > 0 ? 'text-green-600' : grandTotal < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                    {fmtCAD(grandTotal)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          </fieldset>

          {/* Documents Upload */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="block text-xs font-medium text-[#FF6B35] mb-2">
              Documents <span className="text-gray-400 font-normal">(optional)</span>
            </label>

            {storagePath ? (
              // Extra existant : pieces lues et ecrites dans extra_documents.
              <EntityDocumentsPanel
                entityType="extra"
                entityId={extra?.id}
                projectId={projectId}
                readOnly={isViewing}
              />
            ) : (
            <>

            {/* Uploaded file list */}
            {displayFiles?.length > 0 ? (
              <ul className="space-y-1 mb-2">
                {displayFiles?.map((file) => (
                  <li key={file?.name} className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                    {getFileTypeIcon(file?.name)}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-700 truncate">{file?.name}</p>
                      {file?.metadata?.size && (
                        <p className="text-[10px] text-gray-400">{formatFileSize(file?.metadata?.size)}</p>
                      )}
                    </div>
                    {!isViewing && <button
                      type="button"
                      onClick={() => deleteDocFile(file?.name)}
                      className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Icon name="Trash2" size={12} />
                    </button>}
                  </li>
                ))}
              </ul>
            ) : null}

            {uploadError && (
              <div className="mb-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-line">
                {uploadError}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileInputChange}
            />
            {!isViewing && <div
              onClick={() => !uploading && fileInputRef?.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-xl px-4 py-4 text-center cursor-pointer transition-all ${
                isDragOver
                  ? 'border-primary bg-orange-50 scale-[1.01]'
                  : 'border-gray-200 hover:border-primary hover:bg-orange-50/30'
              } ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-1.5">
                  <Icon name="Loader2" size={18} className="animate-spin text-primary" />
                  <p className="text-xs text-gray-500">Uploading...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1.5">
                  <Icon name="Upload" size={18} className={isDragOver ? 'text-primary' : 'text-gray-400'} />
                  <p className="text-xs font-medium text-gray-600">
                    Drop files here or <span className="text-primary">click to upload</span>
                  </p>
                  <p className="text-[10px] text-gray-400">PDF, Excel, Word supported · Max 20MB each</p>
                </div>
              )}
            </div>}
            {pendingFiles?.length > 0 && (
              <p className="mt-1.5 text-[10px] text-gray-400">{t('extras.addModal.filesAfterSave')}</p>
            )}
            </>
            )}
          </div>

        </form>

        {/* Footer */}
        {isViewing ? (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {t('common.close')}
          </button>
          <button
            type="button"
            onClick={() => setIsViewing(false)}
            disabled={editLocked || loadingLines}
            title={editLocked ? t('extras.viewLockedApproved') : undefined}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="Pencil" size={12} />
            {t('common.edit')}
          </button>
        </div>
        ) : (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {t('extras.addModal.cancel')}
          </button>
          <button
            type="submit"
            form="add-extra-form"
            disabled={saving || loadingLines}
            onClick={handleSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
          >
            {saving ? <Icon name="Loader2" size={12} className="animate-spin" /> : <Icon name={isEditMode ? 'Check' : 'Plus'} size={12} />}
            {saving
              ? t('extras.addModal.saving')
              : isEditMode
                ? t('extras.editModal.saveChanges')
                : t('extras.addModal.add')
            }
          </button>
        </div>
        )}

      </div>
    </div>
  );
};

export default AddExtraModal;
